import { MUSEFLOW_ORANGE } from 'Components/Colors';
import { TOUCH_TARGET_MIN_SIZE_PX } from 'Utils/touchTarget';

/**
 * One look for the single-choice controls on Settings > Practice ("What do you
 * play on?" and Score Theme) and wherever they are reused: the same outline,
 * corner, fill and orange selection as the device screen's answer cards, so an
 * answer reads the same everywhere it is asked.
 */
export const CHOICE_RADIUS_PX = 10;
export const CHOICE_GROUP_RADIUS_PX = 12;
export const CHOICE_BORDER = 'rgba(255, 255, 255, 0.16)';
export const CHOICE_BORDER_HOVER = 'rgba(255, 255, 255, 0.34)';
export const CHOICE_FILL = 'rgba(255, 255, 255, 0.03)';
export const CHOICE_FILL_HOVER = 'rgba(255, 255, 255, 0.07)';
export const CHOICE_SELECTED_FILL = 'rgba(202, 124, 98, 0.16)';
export const CHOICE_TEXT = '#CED0D4';
export const CHOICE_SELECTED_TEXT = '#FFFFFF';

const focusRing = { outline: `2px solid ${MUSEFLOW_ORANGE}`, outlineOffset: '2px' } as const;

/**
 * The keyboard focus ring every choice shows. A browser without `:focus-visible`
 * (iOS 15.0 to 15.3) shows it on any focus; one with it shows it for the
 * keyboard only, not on a click or tap.
 */
export const choiceFocusRingSx = {
  '&:focus': focusRing,
  '@supports selector(:focus-visible)': {
    '&:focus': { outline: 'none' },
    '&:focus-visible': focusRing,
  },
} as const;

/** One answer: outlined, orange when chosen. At least a 44px target. */
export const choiceOptionSx = (selected: boolean) =>
  ({
    position: 'relative',
    boxSizing: 'border-box',
    minWidth: 0,
    minHeight: `${TOUCH_TARGET_MIN_SIZE_PX}px`,
    margin: 0,
    border: `1px solid ${selected ? MUSEFLOW_ORANGE : CHOICE_BORDER}`,
    borderRadius: `${CHOICE_RADIUS_PX}px`,
    backgroundColor: selected ? CHOICE_SELECTED_FILL : CHOICE_FILL,
    color: selected ? CHOICE_SELECTED_TEXT : CHOICE_TEXT,
    fontFamily: 'Lato',
    fontWeight: selected ? 700 : 400,
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease',
    '&:hover:not(:disabled):not([aria-disabled="true"])': selected
      ? {}
      : { borderColor: CHOICE_BORDER_HOVER, backgroundColor: CHOICE_FILL_HOVER },
    ...choiceFocusRingSx,
  }) as const;
